import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";
import type * as React from "react";
import { cn } from "~/lib/utils";

const badgeVariants = cva(
	"focus-visible:border-ring focus-visible:ring-ring/50 inline-flex w-fit shrink-0 items-center justify-center gap-1 border px-1.5 text-xs leading-normal font-medium whitespace-nowrap transition-[color,box-shadow] focus-visible:ring-[3px] [&_svg:not([class*='size-'])]:size-4 [&>svg]:pointer-events-none [&>svg]:shrink-0",
	{
		variants: {
			rounded: {
				default: "rounded-lg",
				full: "rounded-full",
			},
			color: {
				red: "border-red-300 bg-red-100 text-red-700 [a&]:hover:bg-red-500/90",
				orange: "border-orange-300 bg-orange-100 text-orange-700 [a&]:hover:bg-orange-500/90",
				amber: "border-amber-300 bg-amber-100 text-amber-700 [a&]:hover:bg-amber-500/90",
				yellow: "border-yellow-300 bg-yellow-100 text-yellow-700 [a&]:hover:bg-yellow-500/90",
				lime: "border-lime-300 bg-lime-100 text-lime-700 [a&]:hover:bg-lime-500/90",
				green: "border-green-300 bg-green-100 text-green-700 [a&]:hover:bg-green-500/90",
				emerald: "border-emerald-300 bg-emerald-100 text-emerald-700 [a&]:hover:bg-emerald-500/90",
				teal: "border-teal-300 bg-teal-100 text-teal-700 [a&]:hover:bg-teal-500/90",
				cyan: "border-cyan-300 bg-cyan-100 text-cyan-700 [a&]:hover:bg-cyan-500/90",
				sky: "border-sky-300 bg-sky-100 text-sky-700 [a&]:hover:bg-sky-500/90",
				blue: "border-blue-300 bg-blue-100 text-blue-700 [a&]:hover:bg-blue-500/90",
				indigo: "border-indigo-300 bg-indigo-100 text-indigo-700 [a&]:hover:bg-indigo-500/90",
				violet: "border-violet-300 bg-violet-100 text-violet-700 [a&]:hover:bg-violet-500/90",
				purple: "border-purple-300 bg-purple-100 text-purple-700 [a&]:hover:bg-purple-500/90",
				pink: "border-pink-300 bg-pink-100 text-pink-700 [a&]:hover:bg-pink-500/90",
				rose: "border-rose-300 bg-rose-100 text-rose-700 [a&]:hover:bg-rose-500/90",
				gray: "border-gray-300 bg-gray-100 text-gray-700 [a&]:hover:bg-gray-500/90",
			},
			status: {
				default: "",
				Removed: "",
				Draft: "",
				Transcoding: "",
				Error: "",
				Scheduled: "",
				Published: "",
				Expired: "",
				Unpublished: "",
				Updating: "",
			},
			variant: {
				default: "bg-primary text-primary-foreground [a&]:hover:bg-primary/90 border-transparent",
				secondary:
					"bg-secondary text-secondary-foreground [a&]:hover:bg-secondary/90 border-transparent",
				destructive:
					"bg-destructive [a&]:hover:bg-destructive/90 focus-visible:ring-destructive/20 dark:focus-visible:ring-destructive/40 border-transparent text-white",
				outline: "text-foreground [a&]:hover:bg-accent [a&]:hover:text-accent-foreground",
				color: "border-transparent",
				status: "border-transparent",
			},
		},
		compoundVariants: [
			{
				variant: "status",
				status: "default",
				class: "bg-[#0F0F0F] text-white",
			},
			{
				variant: "status",
				status: "Removed",
				class: "bg-[#fe6b5b] text-white",
			},
			{ variant: "status", status: "Draft", class: "bg-[#5f4d8b] text-white" },
			{
				variant: "status",
				status: "Transcoding",
				class: "bg-[#9b9b9b] text-white",
			},
			{ variant: "status", status: "Error", class: "bg-[#fe6b5b] text-white" },

			{
				variant: "status",
				status: "Scheduled",
				class: "bg-[#89c42a] text-white",
			},
			{
				variant: "status",
				status: "Published",
				class: "bg-[#89c42a] text-white",
			},
			{
				variant: "status",
				status: "Expired",
				class: "bg-[#89c42a] text-white",
			},
			{
				variant: "status",
				status: "Unpublished",
				class: "bg-[#89c42a] text-white",
			},
			{
				variant: "status",
				status: "Updating",
				class: "bg-blue-600 text-white",
			},
		],
		defaultVariants: {
			rounded: "default",
			status: null,
			variant: null,
		},
	},
);

function Badge({
	className,
	color,
	rounded,
	status,
	variant,
	asChild = false,
	...props
}: React.ComponentProps<"span"> & VariantProps<typeof badgeVariants> & { asChild?: boolean }) {
	const Comp = asChild ? Slot : "span";

	return (
		<Comp
			data-slot="badge"
			className={cn(badgeVariants({ color, rounded, status, variant }), className)}
			{...props}
		/>
	);
}

export { Badge, badgeVariants };
